import {
  PapiDeviation,
  PapiDeviationStudio,
  PapiGallection,
  PapiStudioPagesProducts,
  PapiStudioSellerStatsInit,
  PapiStudioDiscountsInit,
  PapiStudioHomeInit,
  PapiTag,
  PapiUser,
  PapiBoostRestrictions,
  PapiStudioEarningsInit,
  PapiDaSharedDeviationSubmitInit,
  PapiDreamsofartRestrictions,
} from '@wix/da-papi-types';

export type OriginalsData = NonNullable<PapiDeviationStudio['originals']>;
import { FileUploadInfo } from '@wix/da-shared-react/pkg/redux/uploads/types';
import { RunningBatch } from './batchActions';
import { AppData } from './apps';
import { GallerySettingsState } from './gallerySettings';

// NOTE: Keep in sync with helpers/bilogger
export enum StudioSection {
  DRAFTS = 'DRAFTS',
  PUBLISHED = 'PUBLISHED',
  PUBLISHED_GALLERIES = 'PUBLISHED_GALLERIES',
  PUBLISHED_TIERS = 'PUBLISHED_TIERS',
  SCHEDULED = 'SCHEDULED',
  STASH = 'STASH',
  PLAYGROUND = 'PLAYGROUND',
  LITERATURE = 'LITERATURE',
  JOURNALS = 'JOURNALS',
  BOOKMARKS = 'BOOKMARKS',
  SHOP_REPORTS = 'SHOP_REPORTS',
  ORDERS_STATUS = 'ORDERS_STATUS',
  ORDERS_ORIGINALS = 'ORDERS_ORIGINALS',
  PRODUCTS_LOBBY = 'PRODUCTS_LOBBY',
  PRODUCTS_TIER = 'PRODUCTS_TIER',
  PRODUCTS_TIER_UNLISTED = 'PRODUCTS_TIER_UNLISTED',
  PRODUCTS_COMMISSION = 'PRODUCTS_COMMISSION',
  PRODUCTS_ORIGINAL = 'PRODUCTS_ORIGINAL',
  PRODUCTS_ORIGINAL_UNLISTED = 'PRODUCTS_ORIGINAL_UNLISTED',
  PRODUCTS_PREMIUM_GALLERY = 'PRODUCTS_PREMIUM_GALLERY',
  PRODUCTS_PCP = 'PRODUCTS_PCP',
  PRODUCTS_PCP_UNLISTED = 'PRODUCTS_PCP_UNLISTED',
  PRODUCTS_EXCLUSIVE = 'PRODUCTS_EXCLUSIVE',
  PRODUCTS_EXCLUSIVE_UNLISTED = 'PRODUCTS_EXCLUSIVE_UNLISTED',
  SELLER_DISCOUNTS_ACTIVE = 'SELLER_DISCOUNTS_ACTIVE',
  SELLER_DISCOUNTS_SCHEDULED = 'SELLER_DISCOUNTS_SCHEDULED',
  SELLER_DISCOUNTS_ENDED = 'SELLER_DISCOUNTS_ENDED',
  SELLER_DISCOUNTS_ALL = 'SELLER_DISCOUNTS_ALL',
  HOME = 'home',
  APPS = 'apps',
  APPS_UNLISTED = 'apps_unlisted',
  EARNINGS = 'EARNINGS',
}

export enum PlaygroundSubsection {
  DreamUp = 'dreamup',
  Draw = 'draw',
  Video = 'video',
  Inspiration = 'inspiration',
  Wixel = 'wixel',
  NotMounted = 'not-mounted',
}

export enum DiscountsSubsection {
  Active = 'active',
  Scheduled = 'scheduled',
  Ended = 'ended',
  ActiveAndScheduled = 'active_and_scheduled',
  All = 'all',
}

export enum BatchAction {
  EDIT_TITLE = 'edit_title',
  EDIT_TAGS = 'edit_tags',
  EDIT_DESCRIPTION = 'edit_description',
  EDIT_GALLERY = 'edit_gallery',
  EDIT_TIER = 'edit_tier',
  EDIT_SCHEDULE = 'edit_schedule',
  EDIT_LICENSE = 'edit_license',
  EDIT_DISPLAY_OPTIONS = 'edit_display_options',
  EDIT_COMMENTS = 'edit_comments',
  EDIT_LABELS = 'edit_labels',
  EDIT_PRESET = 'edit_preset',
  BOOL_AI_ADD = 'bool_ai_add',
  BOOL_AI_DEL = 'bool_ai_del',
  BOOL_NOAI_ADD = 'bool_noai_add',
  BOOL_NOAI_DEL = 'bool_noai_del',
  BOOL_MATURE_ADD = 'bool_mature_add',
  BOOL_MATURE_DEL = 'bool_mature_del',
  OTHER_DELETE = 'other_delete',
  OTHER_UNPUBLISH = 'other_unpublish',
  OTHER_MOVE_TO_FOLDER = 'other_move_to_folder',
  OTHER_CREATE_FOLDER_FROM_SELECTION = 'other_create_folder_from_selection',
  OTHER_MERGE = 'other_merge',
  OTHER_MOVE_TO_STASH = 'other_move_to_stash',
  OTHER_MOVE_TO_DRAFTS = 'other_move_to_drafts',
  OTHER_SELL = 'other_sell',
}

export enum TableLayout {
  LIST = 'list',
  GRID = 'grid',
}

export enum VideoModel {
  HAILUO = 'hailuo',
  VEO = 'veo',
  WAN = 'wan',
  SEEDANCE = 'seedance',
  GROK = 'grok',
}

export type VideoCreatorPendingGeneration = {
  generationId: number;
  modelType: VideoModel;
  prompt: string;
  aspectRatio: string;
  duration: number;
  referenceDeviation?: PapiDeviation;
  sound: boolean;
};

export type VideoAspectRatio = '4:3' | '16:9' | '9:16' | '1:1' | '3:4' | '21:9';

export enum VideoCreatorErrorCode {
  NO_ERROR = -100,

  OTHER = -1,
  VALIDATION_ERROR = 0,
  UPLOAD_ERROR = -2,
  UPLOADS_TEMPORARILY_DISABLED = -3,
  ILLEGAL_PROMPT = 1,
  INSUFFICIENT_POINTS_BALANCE = 2,
  OUT_OF_SPACE = 3,
  OUT_OF_FILE_QUOTA = 4,
  TOO_FAST = 5,
  TOS_VIOLATION = 6,
}

export interface StudioState {
  params: {
    section: StudioSection;
    deviationIds?: string[];
    folderIds?: string[];
    subfolderId?: string;
    tierId?: string;
    searchQuery?: string;
    contentType?: string; // used in studio/pages/published
    unlisted?: boolean; // used in studio/pages/products & studio/pages/apps
  };
  table: {
    streamId: string;
    pageSize: number;
    layout: TableLayout;
  };
  resultsTotalCount: number;
  freeCapacity: number;
  maxCapacity: number;
  featuredFolderId?: number;
  savedSubmissionsFolderId?: number;
  selectedFolder?: PapiGallection;
  parentFolder?: PapiGallection;
  selection: {
    selectedRows: number[];
    lastSelectedRow: number | null;
  };
  galleries?: PapiGallection[];
  tags: { [deviationId: number]: PapiTag[] };
  originalsData?: { [deviationId: number]: OriginalsData };
  tiers?: PapiDeviation[];
  batchAction?: BatchAction;
  batchActionLoading?: boolean;
  runningBatches: RunningBatch[];
  playground: {
    subsection: PlaygroundSubsection;
    isDrawingsTabVisible: boolean;
  };
  discounts?: {
    subsection: DiscountsSubsection;
    limitations: PapiStudioDiscountsInit['limitations'];
  };
  breadcrumbs?: {
    results?: PapiGallection[];
    status: 'idle' | 'busy';
  };
  folders?: {
    status: 'idle' | 'busy';
  };
  products?: {
    counts: NonNullable<PapiStudioPagesProducts['counts']>;
  };
  reports?: {
    soldProducts?: PapiStudioSellerStatsInit['soldProducts'];
    soldSubscriptions?: PapiStudioSellerStatsInit['soldSubscriptions'];
    topSellingProducts?: PapiStudioSellerStatsInit['topSellingProducts'];
    topCustomers?: PapiStudioSellerStatsInit['topCustomers'];
    soldExclusives?: PapiStudioSellerStatsInit['soldExclusives'];
    topSubscribers?: PapiStudioSellerStatsInit['topSubscribers'];
    subsCancellationReasons?: PapiStudioSellerStatsInit['subsCancellationReasons'];
  };
  earnings?: Omit<
    PapiStudioEarningsInit,
    'session' | 'max_capacity' | 'free_capacity' | 'bookmarks' | 'transactions'
  >;
  home?: {
    counts?: PapiStudioHomeInit['counts'];
    currencies?: PapiStudioHomeInit['currencies'];
  };
  apps?: {
    hasApps?: boolean;
    appData?: {
      [deviationId: number]: AppData;
    };
  };
  orders?: {
    hasOrders?: boolean;
  };
  boostRestrictions?: PapiBoostRestrictions;
  newModalLoading?: boolean;
  duperbrowseStreamVersion: number;
  gallerySettings?: GallerySettingsState;
  videoCreator: {
    prompt: string;
    model: VideoModel;
    aspectRatio: VideoAspectRatio;
    duration: number;
    sound: boolean;
    referenceDeviation?: PapiDeviation;
    errorCode: VideoCreatorErrorCode;
    status: 'idle' | 'busy';
    restrictions?: PapiDreamsofartRestrictions;
    pointsPackages: {
      points: number;
      price: number;
      subproductId: number;
    }[];
    pricePoints?: number;
    lowestPricePoints?: number;
    generationCostModel?: string;
    isCalculatingCost: boolean;
    pendingGenerations: VideoCreatorPendingGeneration[];
    modelsExpanded: boolean;
  };
}

export interface StashState {
  isVisitorView: boolean;
  folder?: PapiGallection;
}

export interface StandaloneSubmitState {
  initResponse?: PapiDaSharedDeviationSubmitInit;
}

export interface CompletedUploadWithDeviation extends FileUploadInfo {
  deviation?: PapiDeviation;
}

export interface AppState {
  editor?: {
    deviation?: PapiDeviation;
    group?: PapiUser;
    type?: 'journal' | 'literature';
  };
  studio?: StudioState;
  stash?: StashState;
  submit?: StandaloneSubmitState;
}
